import React from 'react';
import { useNavigate } from 'react-router';
import {
    Grid2 as Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    useTheme,
    useMediaQuery,
    Chip,
    Pagination,
    styled,
    Backdrop,
    CircularProgress,
    Modal,
    Box,
    Button,
    Autocomplete,
    TextField
} from '@mui/material';
import GridLayout from '../../components/GridLayout/GridLayout';
import MenuMoreVert from '../../components/Menu/MoreVert';
import RoleButton from '../../components/RoleButton/RoleButton';
import SearchInput from '../../components/SearchInput/SearchInput';
import NoData from '../../components/Placeholders/NoData';
import { ROLES } from '../../assets/constants/constants';
import { formatDate } from '../../assets/utils/utils';
import api from '../../api/api';
import { ArrowForward } from '@mui/icons-material';
import Notification from '../../components/Notification/Notification';

const STATUS = {
    1: { label: 'Activo', color: 'success' },
    0: { label: 'Inactivo', color: 'default' },
};

const StylizedChip = styled(Chip)(({ theme }) => ({
    color: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    width: '100%',
}));

const splitDataIntoChunks = (data, chunkSize = 10) => {
    let result = [];
    for (let i = 0; i < data.length; i += chunkSize) {
        result.push(data.slice(i, i + chunkSize));
    }
    return result;
}

const UserList = () => {
    const navigate = useNavigate();

    const [loading, setLoading] = React.useState(true);
    const [users, setUsers] = React.useState([]);
    const [filteredUsers, setFilteredUsers] = React.useState([]);
    const [page, setPage] = React.useState(1);
    const [lastPage, setLastPage] = React.useState(1);
    const [open, setOpen] = React.useState(false)
    const [jobPositions, setJobPositions] = React.useState(null)
    const [selectedUser, setSelectedUser] = React.useState(null)
    const [newBoss, setNewBoss] = React.useState("")
    const [notificationProps, setNotificationProps] = React.useState({
        open: false,
        severity: 'success',
        message: '',
    });
    const [waiting, setWaiting] = React.useState(false);

    const handleClose = () => {
        setOpen(false)
    } 

    const getAllJobs = () => {
        api.get('job_position/positions').then(res => res.ok && setJobPositions(res.data))
    }

    React.useEffect(() => {
        getAllJobs();
    }, [])

    const openChangeBoss = (id) => {
        const user = users.find(group => group.find(user => user.pk_user_id === id)).find(user => user.pk_user_id === id)
        if(!user.fk_job_position_id) return alert("Este usuario no cuenta con un puesto asignado. Para poder continuar hay que asignarle un puesto.");
        setSelectedUser(user)
        setOpen(true)
    }

    const handleChangeBoss = (event, value) => {
        setNewBoss(value)
    }

    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

    const handleCreate = React.useCallback(e => {
        e.preventDefault();
        navigate('crear');
    }, [navigate]);

    const handleEdit = React.useCallback((e, userid) => {
        e.preventDefault();
        navigate(`editar/${userid}`);
    }, [navigate]);

    const handleStatus = React.useCallback(async (e, id, status) => {
        const confirm = window.confirm(`¿Está seguro de ${status ? 'desactivar' : 'activar'} el usuario?`);
        if (!confirm) return;
        e.preventDefault();
        try {
            const newStatus = status ? 0 : 1;
            const { ok } = await api.put('user/status', { id, status: newStatus, });
            if (ok) {
                setUsers(prevState => prevState && prevState.map(items => items && items.map(item => (
                    item.pk_user_id === id ? { ...item, is_active: newStatus } : item
                ))));
                setFilteredUsers(prevState => prevState && prevState.map(items => items && items.map(item => (
                    item.pk_user_id === id ? { ...item, is_active: newStatus } : item
                ))));
            }
        }
        catch (error) {
            console.log('Error al actualizar el estatus:', error);
        }
    }, []);

    const handleSaveChanges = async () => {
        if(!selectedUser.fk_job_position_id) return alert("Este usuario no cuenta con un puesto asignado. Para poder continuar hay que asignarle un puesto.");
        const confirm = window.confirm(`¿Está seguro de cambiar el jefe directo de ${selectedUser.full_name}?`);
        if (!confirm) return;

        setWaiting(true);
        const position_id = selectedUser.fk_job_position_id;
        const currentPosition = jobPositions.find(position => position.pk_job_position_id === position_id)
        const body = {
            job_position:currentPosition.job_position,
            job_position_area_id:currentPosition.fk_job_position_area_id,
            job_position_department_id:currentPosition.fk_job_position_department_id,
            job_position_office_id:currentPosition.fk_job_position_office_id,
            job_position_type_id:currentPosition.fk_job_position_type_id,
            job_position_status_id:currentPosition.fk_job_position_status_id,
            job_position_admin_status_id:currentPosition.fk_job_position_admin_status_id,
            job_position_parent_id:newBoss.id.toString(),
            publish_date:currentPosition.publish_date
        }
        
        const { ok,  message } = await api.put(`job_position/${position_id}`, body)
        if(ok) handleNotification('success', message);
        else handleNotification('error', message);
        setWaiting(false);
        fetchData()
    }

    const fetchData = async () => {
        try {
            const { ok, users } = await api.get('users');
            if (ok) {
                setUsers(splitDataIntoChunks(users));
                setFilteredUsers(splitDataIntoChunks(users));
            }
        }
        catch (error) {
            console.log('Error al cargar la información: ', error);
        }
        finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetchData();
    }, []);

    const handleNotification = React.useCallback((severity, message, redirectTo = null) => {
            setNotificationProps({
                open: true,
                severity,
                message,
                redirectTo,
                duration: 3000,
            });
            setOpen(false)
            setSelectedUser(undefined)
            setNewBoss(undefined)
        }, []);

    if (loading) return (<Backdrop open={loading} invisible><CircularProgress /></Backdrop>);

    return (
        <>
        <GridLayout>
            <Grid size={12}>
                <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>GESTION DE USUARIOS</Typography>
                <SearchInput
                    data={users}
                    filters={{
                        full_name: 'NOMBRE',
                        username: 'USUARIO',
                        job_position: 'PUESTO',
                        job_position_area: 'ÁREA',
                        job_position_department: 'DEPARTAMENTO',
                        job_position_office: 'OFICINA',
                    }}
                    onFilter={setFilteredUsers}
                    onPage={setPage}
                    lastPage={lastPage}
                    placeholder="Nombre, Usuario, Puesto, Departamento, Oficina..."
                    sx={{ mt: 2, float: 'right', width: isMediumScreen ? '100%' : '33%', }}
                />
                <RoleButton
                    rolesAllowed={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.COMMUNITY_MANAGER]}
                    sx={{ mt: 2, }}
                    onClick={handleCreate}
                    fullWidth={isMediumScreen}>Nuevo Usuario</RoleButton>
                {filteredUsers.length > 0 ?
                    <>
                        <TableContainer component={Paper} className="mt-1">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center">
                                            <Typography variant="body1" fontWeight="bold" color="primary">NOMBRE</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1" fontWeight="bold" color="primary">USUARIO</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1" fontWeight="bold" color="primary">TIPO DE VACANTE</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1" fontWeight="bold" color="primary">PUESTO</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1" fontWeight="bold" color="primary">ÁREA</Typography>
                                        </TableCell>
                                        <TableCell align="center" width={200}>
                                            <Typography variant="body1" fontWeight="bold" color="primary">JEFE DIRRECTO</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1" fontWeight="bold" color="primary">DEPARTAMENTO</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1" fontWeight="bold" color="primary">OFICINA</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1" fontWeight="bold" color="primary">FECHA DE CREACIÓN</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1" fontWeight="bold" color="primary">ÚLTIMO ACCESO</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1" fontWeight="bold" color="primary">TIPO DE USUARIO</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1" fontWeight="bold" color="primary">ESTATUS</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1" fontWeight="bold" color="primary">ACCIONES</Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredUsers[page - 1] && filteredUsers[page - 1].map((row, i) => (
                                        <TableRow key={row.pk_user_id}>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold" color="primary">{row.full_name}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold" color="primary">{row.username}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold" color="primary">{row.job_type ?? "-----"}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold" color="primary">{row.job_position}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold" color="primary">{row.job_position_area}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold" color="primary">{row.boss ?? "-----"}</Typography>
                                                <Typography fontSize={10} color="secondary">{row.boss_position ?? "-----"}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold" color="primary">{row.job_position_department}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold" color="primary">{row.job_position_office}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold" color="primary">{formatDate(row.created_at)}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold" color="primary">{formatDate(row.last_access_at)}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" fontWeight="bold" color="primary">{row.user_type}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <StylizedChip label={STATUS[row.is_active]?.label} color={STATUS[row.is_active]?.color} sx={{ width: '100%', }} />
                                            </TableCell>
                                            <TableCell align="center">
                                                <MenuMoreVert options={[
                                                    { label: 'Editar', onClick: (e) => handleEdit(e, row.pk_user_id), },
                                                    { label: Boolean(parseInt(row.is_active)) ? 'Desactivar' : 'Activar', onClick: (e) => handleStatus(e, row.pk_user_id, Boolean(parseInt(row.is_active))), title: !Boolean(parseInt(row.is_active)) ? '' : 'Se liberará además la vacante asociada al usuario.', },
                                                    { label: 'Cambiar jefe directo', onClick: () => openChangeBoss(row.pk_user_id) }
                                                ]} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Pagination
                            count={(filteredUsers && filteredUsers.length) || 1}
                            color="primary"
                            page={page}
                            onChange={(e, page) => {
                                setPage(page);
                                setLastPage(page);
                            }}
                            sx={{ mt: 2, float: 'right', }} />
                    </>
                    :
                    <NoData />}
            </Grid>
        </GridLayout>
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="parent-modal-title"
            aria-describedby="parent-modal-description">
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 700,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    pt: 2,
                    px: 2,
                    pb: 3,
                    borderRadius: .8
                }}>
                    <Typography variant="h6" fontWeight="bold" color="primary">Cambiar jefe directo</Typography>
                    <table width={"100%"} style={{ marginTop: 30, marginBottom: 30 }}>
                        <thead>
                            <tr>
                                <td>
                                    <Typography variant="body2" color="primary">Jefe Directo Actual</Typography>
                                </td>
                                <td></td>
                                <td align='right'>
                                    <Typography variant="body2" color="primary">Nuevo Jefe Directo</Typography>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <Typography variant="body2" fontWeight="bold" color="primary">{selectedUser?.boss ?? "-----"}</Typography>
                                    <Typography fontSize={10} color="secondary">{selectedUser?.boss_position ?? "-----"}</Typography>
                                </td>
                                <td><ArrowForward /></td>
                                <td align='right'>
                                    <Autocomplete
                                        disablePortal
                                        onChange={handleChangeBoss}
                                        options={jobPositions.map(position => ({
                                            id: position.pk_job_position_id,
                                            label: `${position.job_position}|${position.user_full_name}|${position.job_position_office}|${position.job_position_status}|${position.created_at}`,
                                            name : position.job_position,
                                            office: position.job_position_office,
                                            status: position.job_position_status,
                                            created_at: position.created_at,
                                            full_name: position.user_full_name

                                        }))}
                                        sx={{ width: 400 }}
                                        size='small'
                                        getOptionLabel={option => `${option.name} - ${option.full_name}`}
                                        renderOption={(props, option) => {
                                            const { key, ...optionProps } = props
                                            const {name, office, status, created_at, full_name} = option
                                            return (
                                                <Box
                                                    key={`${name}|${full_name}|${office}|${status}|${created_at}`}
                                                    {...optionProps}
                                                    style={{ flexDirection: "column", alignItems: "start" }}
                                                >
                                                    <Typography textAlign="left" variant="body2" fontWeight="bold" color="primary">{name}</Typography>
                                                    {full_name && <Typography textAlign="left" fontWeight="bold" fontSize={10} color="secondary">{full_name}</Typography>}
                                                    {!full_name.trim() && status === "Disponible" && <Typography textAlign="left" fontWeight="bold" fontSize={10} color="secondary">{'[VACANTE]'}</Typography>}
                                                    <Typography textAlign="left" fontSize={10} color="secondary">{office}</Typography>
                                                </Box>
                                            )
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Selecciona una opción" />
                                        )}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div style={{ textAlign: "right" }}>
                        <Button variant="contained" onClick={handleSaveChanges} loading={waiting} disabled={waiting}>
                            Guardar cambios
                        </Button>
                    </div>
                </Box>
            </Modal>
            <Notification
            {...notificationProps}
            onclose={() => setNotificationProps((prevState) => ({ ...prevState, open: false, }))}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }} />
        </>
    )
};

export default UserList;